import { useCallback, useRef, useState } from "react";
import Editor, { OnMount } from "@monaco-editor/react";
import { ASK_MAX_LINES, AskSelection } from "../lib/askMessage";

/**
 * Monaco-backed read-only code viewer.
 *
 * Select-to-ask (issue #56): while the reviewer has a non-empty selection,
 * an "Ask Claude" overlay appears in the bottom-right of the editor
 * (bottom, not top, so it never fights Monaco's find widget). Clicking it
 * hands the selection (line range + text) to `onAskClaude`; the Reviewer
 * screen routes it into the chat. The overlay sits outside Monaco's DOM,
 * so clicking it doesn't collapse the selection.
 */

type MonacoEditor = Parameters<OnMount>[0];

/** Plain copy of the (normalized) selection range. Only the range lives in
 *  state — the selected TEXT is extracted once at click time, because
 *  onDidChangeCursorSelection fires per drag-pixel and copying a large
 *  selection's text on every event would jank the drag. */
type CaptureRange = {
  startLineNumber: number;
  startColumn: number;
  endLineNumber: number;
  endColumn: number;
};

/** Hoisted so the memoized Editor isn't re-rendered by our selection state
 *  churn (a fresh options object per render would defeat its memo). */
const EDITOR_OPTIONS = {
  readOnly: true,
  minimap: { enabled: false },
  wordWrap: "on",
  scrollBeyondLastLine: false,
  renderWhitespace: "none",
  fontSize: 13,
} as const;

export default function CodeViewer(props: {
  language: string | null;
  content: string;
  truncated: boolean;
  onAskClaude?: (sel: AskSelection) => void;
}) {
  const [range, setRange] = useState<CaptureRange | null>(null);
  const editorRef = useRef<MonacoEditor | null>(null);

  const handleMount: OnMount = useCallback((editor) => {
    editorRef.current = editor;
    editor.onDidChangeCursorSelection((e) => {
      const model = editor.getModel();
      if (!model || e.selection.isEmpty()) {
        setRange(null);
        return;
      }
      // Monaco reports line-boundary selections (triple-click, gutter drag)
      // as ending at column 1 of the NEXT line. Normalize so the label and
      // the sent message never claim a line whose text isn't selected.
      let endLine = e.selection.endLineNumber;
      let endColumn = e.selection.endColumn;
      if (endColumn === 1 && endLine > e.selection.startLineNumber) {
        endLine -= 1;
        endColumn = model.getLineMaxColumn(endLine);
      }
      setRange({
        startLineNumber: e.selection.startLineNumber,
        startColumn: e.selection.startColumn,
        endLineNumber: endLine,
        endColumn,
      });
    });
  }, []);

  const onAsk = useCallback(() => {
    const model = editorRef.current?.getModel();
    if (!model || !range) return;
    // Capture at most one line past the embed cap — enough for
    // buildAskMessage to detect the overflow and add its truncation marker,
    // without hauling a whole-file selection into React state.
    const capLine = Math.min(
      range.endLineNumber,
      range.startLineNumber + ASK_MAX_LINES,
    );
    const text = model.getValueInRange({
      ...range,
      endLineNumber: capLine,
      endColumn:
        capLine === range.endLineNumber
          ? range.endColumn
          : model.getLineMaxColumn(capLine),
    });
    props.onAskClaude?.({
      startLine: range.startLineNumber,
      endLine: range.endLineNumber,
      text,
    });
  }, [range, props.onAskClaude]);

  return (
    <div className="flex h-full flex-col">
      {props.truncated && (
        <div className="border-b border-yellow-200 bg-yellow-50 px-3 py-1 text-xs text-yellow-900">
          File truncated to 1 MiB — open the raw file for the full contents.
        </div>
      )}
      <div className="relative flex-1">
        <Editor
          theme="vs"
          language={props.language ?? "plaintext"}
          value={props.content}
          onMount={handleMount}
          options={EDITOR_OPTIONS}
        />
        {range && props.onAskClaude && (
          <button
            onClick={onAsk}
            className="absolute bottom-4 right-6 z-10 rounded bg-ink-700 px-2.5 py-1 text-xs text-white shadow hover:bg-ink-900"
          >
            Ask Claude · L{range.startLineNumber}–{range.endLineNumber}
          </button>
        )}
      </div>
    </div>
  );
}
